// Shared chrome for every cabinet, in the register of a 2001 online casino opened inside a
// Windows XP program: Luna menu bar, an address strip, a status bar, and a client area that is a
// casino web page of the era — green felt, gold serif, beveled PLAY buttons, a marquee, 88x31
// badges and LED counters (the real XP Minesweeper digit bitmaps).
//
// HERO: the address bar reads http://casino.internetcoin.vip/…htm and the DIAL-UP table's modem
// LEDs blink while a round is pending — you are surfing a casino in 2001.
//
// The HOUSE RULES panel is not decoration. Every cabinet prints its own edge before the play
// button, in the same place, in the same words, and the one cabinet whose coins are genuinely
// fair is the only one allowed to say so. Getting that wrong is not a UI bug, it is a lie.
//
// Control recipes (buttons, group boxes, sunken fields, status bar) follow XP.css by botoxparty
// (MIT), scoped to this window rather than imported, so the rest of the desktop is untouched.
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { CHIP, formatUnits, parseUnits } from 'lib/casino';
import earth from 'assets/windowsIcons/earth.png';
import iePaper from 'assets/windowsIcons/ie-paper.png';
import digit0 from 'assets/minesweeper/digit0.png';
import digit1 from 'assets/minesweeper/digit1.png';
import digit2 from 'assets/minesweeper/digit2.png';
import digit3 from 'assets/minesweeper/digit3.png';
import digit4 from 'assets/minesweeper/digit4.png';
import digit5 from 'assets/minesweeper/digit5.png';
import digit6 from 'assets/minesweeper/digit6.png';
import digit7 from 'assets/minesweeper/digit7.png';
import digit8 from 'assets/minesweeper/digit8.png';
import digit9 from 'assets/minesweeper/digit9.png';
import digitDash from 'assets/minesweeper/digit-.png';

// ── tokens ───────────────────────────────────────────────────────────────────
export const T = {
  felt: '#0b5d33',
  feltDark: '#063b20',
  feltLine: '#0f7a43',
  gold: '#ffd86b',
  goldDeep: '#c9a227',
  goldInk: '#4a3600',
  gray: '#ece9d8',
  grayDark: '#aca899',
  grayLine: '#919b9c',
  navy: '#0a246a',
  navyDeep: '#05123a',
  red: '#c8102e',
  cream: '#fffbe6',
  ink: '#000',
  led: '#ff3b3b',
  infobar: '#ffffe1',
};
export const FONT = {
  ui: "Tahoma, Verdana, 'Noto Sans', sans-serif",
  web: "Verdana, Tahoma, 'Noto Sans', sans-serif",
  serif: "'Times New Roman', Times, Georgia, serif",
  mono: "'Lucida Console', Monaco, 'Courier New', monospace",
  sticker: "Impact, 'Arial Black', 'Helvetica Neue', sans-serif",
};

// Legacy names, still exported for anything that imported the old palette.
export const LIME = T.gold;
export const LIME_DIM = T.goldDeep;
export const INK = T.ink;
export const GREEN = '#00c805';

export function fmtChip(v, places) {
  if (v === null || v === undefined) return '—';
  return formatUnits(v, CHIP.decimals, places === undefined ? 4 : places);
}

/**
 * Chip amounts here span orders of magnitude: a 0.15 bankroll and a 500 one want different
 * precision. A flat 2 places hides every real move on a small house — three losing bets in a
 * row all rounded to "0.15", which reads as a broken casino rather than a working one.
 */
export function fmtChipAuto(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v) / 10 ** CHIP.decimals;
  const places = n >= 1000 ? 2 : n >= 1 ? 3 : 5;
  return formatUnits(v, CHIP.decimals, places);
}

export function fmtMult(bps) {
  return `${(Number(bps) / 10000).toFixed(2)}x`;
}

export function fmtPctFromWad(wad) {
  if (wad === null || wad === undefined) return '—';
  const n = Number(wad) / 1e18;
  if (n >= 0.1) return `${(n * 100).toFixed(1)}%`;
  if (n >= 0.001) return `${(n * 100).toFixed(2)}%`;
  return `${(n * 100).toFixed(4)}%`;
}

// ── shared CSS fragments ─────────────────────────────────────────────────────

/** A Luna push button, after XP.css. */
export const xpButton = css`
  box-sizing: border-box;
  border: 1px solid #003c74;
  border-radius: 3px;
  background: linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4);
  color: #000;
  font: 11px ${FONT.ui};
  min-width: 75px;
  min-height: 23px;
  padding: 0 12px;
  cursor: pointer;
  &:not(:disabled):hover {
    box-shadow: inset -1px 1px #fff0cf, inset 1px 2px #fdd889, inset -2px 2px #fbc761, inset 2px -2px #e5a01a;
  }
  &:not(:disabled):active {
    background: linear-gradient(180deg, #cdcac3, #e3e3db 8%, #e5e5de 94%, #f2f2f1);
    box-shadow: none;
  }
  &:focus {
    outline: 1px dotted #000;
    outline-offset: -4px;
  }
  &:disabled {
    color: #a0a0a0;
    border-color: #c0c0c0;
    background: #f1f1ec;
    cursor: default;
  }
`;

/** A sunken white field, the classic Win32 read-only edit. */
export const sunken = css`
  background: #fff;
  border: 1px solid #7f9db9;
  box-shadow: inset 1px 1px 0 #d6dde5;
  padding: 3px 5px;
  font: 11px ${FONT.ui};
  color: #000;
`;

/** A 2001 casino-site gold bevel: the "PLAY NOW!" button that was on every gambling site. */
export const goldBevel = css`
  background: linear-gradient(180deg, #fff3c4 0%, #ffd86b 38%, #d8a72a 62%, #b8860b 100%);
  border: 2px outset #f5d87a;
  color: #3b2a00;
  text-shadow: 0 1px 0 #fff2c0;
  font-family: ${FONT.web};
  font-weight: 700;
`;

export const XpButton = styled.button`
  ${xpButton}
`;

// ── program chrome ───────────────────────────────────────────────────────────

const PAGES = {
  lobby: 'lobby.htm',
  blueScreen: 'tables/bluescreen.htm',
  mines: 'tables/minesweeper.htm',
  dialUp: 'tables/dialup.htm',
  coinFlip: 'tables/coinflip.htm',
  roulette: 'tables/roulette.htm',
  dice: 'tables/dice.htm',
  wheel: 'tables/wheel.htm',
  slots: 'tables/jackpot.htm',
};

/**
 * The XP program around the page: menu bar, address strip, scrolling client area, status bar.
 * `view` is null for the lobby or a cabinet key. Menus are real, small and useful.
 */
export function Chrome({ casino, view, cabinets, onLobby, onOpenCabinet, onExit, onAbout, children }) {
  const [menu, setMenu] = useState(null);
  const barRef = useRef(null);

  useEffect(() => {
    if (!menu) return undefined;
    const close = e => {
      if (barRef.current && barRef.current.contains(e.target)) return;
      setMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menu]);

  const pick = fn => () => {
    setMenu(null);
    if (fn) fn();
  };

  const menus = {
    Game: [
      { label: 'Lobby', on: onLobby },
      'sep',
      ...(cabinets || []).map(c => ({ label: c.name, on: () => onOpenCabinet(c.key) })),
      'sep',
      { label: 'Exit', on: onExit },
    ],
    Cashier: [
      { label: `Credits: ${fmtChip(casino.balance, 2)} ${CHIP.symbol}`, disabled: true },
      'sep',
      ...(casino.practice
        ? [{ label: 'Reset practice chips', on: casino.resetPractice }]
        : casino.account
        ? [{ label: 'Wallet connected', disabled: true }]
        : [{ label: casino.hasWallet ? 'Connect wallet…' : 'No wallet found', on: () => casino.connect(), disabled: !casino.hasWallet }]),
    ],
    Help: [{ label: 'About Internet Casino…', on: onAbout }],
  };

  return (
    <Program>
      <div className="xp__menubar" ref={barRef}>
        {Object.keys(menus).map(name => (
          <div key={name} className={`xp__menu${menu === name ? ' open' : ''}`}>
            <button
              type="button"
              className="xp__menu__title"
              onMouseDown={e => {
                e.preventDefault();
                setMenu(menu === name ? null : name);
              }}
              onMouseEnter={() => menu && setMenu(name)}
            >
              <u>{name[0]}</u>
              {name.slice(1)}
            </button>
            {menu === name ? (
              <div className="xp__menu__drop">
                {menus[name].map((item, i) =>
                  item === 'sep' ? (
                    <div key={i} className="xp__menu__sep" />
                  ) : (
                    <button
                      key={i}
                      type="button"
                      className="xp__menu__item"
                      disabled={item.disabled}
                      onClick={pick(item.on)}
                    >
                      {item.label}
                    </button>
                  ),
                )}
              </div>
            ) : null}
          </div>
        ))}
        <span className="xp__menubar__brand">Internet Casino</span>
      </div>

      <div className="xp__address">
        <span className="xp__address__label">Address</span>
        <div className="xp__address__field">
          <img src={iePaper} alt="" />
          <span>http://casino.internetcoin.vip/{PAGES[view || 'lobby']}</span>
        </div>
        <button type="button" className="xp__address__go" onClick={onLobby}>
          → Go
        </button>
      </div>

      <div className="xp__client">{children}</div>

      <div className="xp__status">
        <div className="xp__status__field xp__status__done">
          <img src={iePaper} alt="" />
          Done
        </div>
        <div className="xp__status__field xp__status__credits">
          Credits:{' '}
          <span className="cs__chip">
            {fmtChip(casino.balance, 2)} {CHIP.symbol}
          </span>
        </div>
        <div className="xp__status__field xp__status__zone">
          <img src={earth} alt="" />
          {casino.practice ? 'Practice · nothing on chain' : casino.live ? 'Internet' : 'Floor closed'}
        </div>
      </div>
    </Program>
  );
}

const Program = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${T.gray};
  font: 11px ${FONT.ui};
  color: #000;
  -webkit-font-smoothing: antialiased;

  .xp__menubar {
    display: flex;
    align-items: center;
    height: 20px;
    padding: 0 2px;
    background: ${T.gray};
    border-bottom: 1px solid #fff;
    position: relative;
    z-index: 30;
  }
  .xp__menu {
    position: relative;
  }
  .xp__menu__title {
    border: 0;
    background: none;
    font: 11px ${FONT.ui};
    color: #000;
    padding: 2px 7px;
    cursor: default;
  }
  .xp__menu.open .xp__menu__title,
  .xp__menu__title:hover {
    background: #b0d12b;
    color: #000;
  }
  .xp__menu__drop {
    position: absolute;
    top: 20px;
    left: 0;
    min-width: 190px;
    background: #fff;
    border: 1px solid #aca899;
    box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.35);
    padding: 2px;
    z-index: 40;
  }
  .xp__menu__item {
    display: block;
    width: 100%;
    text-align: left;
    border: 0;
    background: none;
    font: 11px ${FONT.ui};
    color: #000;
    padding: 3px 18px 3px 22px;
    cursor: default;
    white-space: nowrap;
  }
  .xp__menu__item:not(:disabled):hover {
    background: #b0d12b;
  }
  .xp__menu__item:disabled {
    color: #7f7f7f;
  }
  .xp__menu__sep {
    height: 1px;
    background: #aca899;
    margin: 2px 2px 2px 22px;
  }
  .xp__menubar__brand {
    margin-left: auto;
    padding-right: 8px;
    color: #6d6d6d;
    font-size: 10px;
  }

  .xp__address {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 2px 4px 3px;
    border-top: 1px solid #fff;
    border-bottom: 1px solid ${T.grayDark};
    background: ${T.gray};
  }
  .xp__address__label {
    color: #000;
    padding: 0 2px;
  }
  .xp__address__field {
    ${sunken};
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 5px;
    height: 21px;
    padding: 0 4px;
    overflow: hidden;
    white-space: nowrap;
    color: #000;
  }
  .xp__address__field img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  .xp__address__go {
    ${xpButton};
    min-width: 0;
    min-height: 21px;
    padding: 0 8px;
  }

  .xp__client {
    flex: 1;
    min-height: 0;
    overflow: auto;
    background: ${T.felt}
      repeating-linear-gradient(45deg, rgba(0, 0, 0, 0.06) 0 2px, transparent 2px 6px);
    color: ${T.cream};
    font-family: ${FONT.web};
    position: relative;
  }

  .xp__status {
    display: flex;
    gap: 2px;
    padding: 2px 2px 2px;
    border-top: 1px solid #fff;
    background: ${T.gray};
    font-size: 11px;
  }
  .xp__status__field {
    box-shadow: inset -1px -1px #fff, inset 1px 1px #aca899;
    padding: 2px 5px;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    overflow: hidden;
  }
  .xp__status__field img {
    width: 14px;
    height: 14px;
  }
  .xp__status__done {
    flex: 1;
  }
  .xp__status__credits .cs__chip {
    font-weight: 700;
  }
  .xp__status__zone {
    min-width: 120px;
  }
`;

// ── page furniture ───────────────────────────────────────────────────────────

/** The IE6-style information bar: pale yellow, one icon, one sentence that matters. */
export function Infobar({ kind, className, children }) {
  return (
    <Bar className={`${className || ''} kind-${kind || 'info'}`}>
      <span className="ib__icon" aria-hidden="true">
        {kind === 'warn' ? '!' : kind === 'error' ? '×' : 'i'}
      </span>
      <div className="ib__text">{children}</div>
    </Bar>
  );
}

const Bar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: ${T.infobar};
  color: #000;
  border-bottom: 1px solid #000;
  padding: 5px 8px 6px;
  font: 11px ${FONT.ui};
  line-height: 1.5;
  b {
    font-weight: 700;
  }
  .ib__icon {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #8fb9ff, #1c4fd6 70%);
    color: #fff;
    font: bold 11px ${FONT.serif};
    font-style: italic;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
  }
  &.kind-warn .ib__icon {
    border-radius: 0;
    clip-path: polygon(50% 0, 100% 100%, 0 100%);
    background: linear-gradient(#ffe066, #e6a800);
    color: #000;
    font-style: normal;
  }
  &.kind-error {
    background: #ffe8e8;
  }
  &.kind-error .ib__icon {
    background: radial-gradient(circle at 35% 30%, #ff9a9a, #b80000 70%);
    font-style: normal;
  }
`;

const DIGITS = [digit0, digit1, digit2, digit3, digit4, digit5, digit6, digit7, digit8, digit9];

/** An LED counter built from the XP Minesweeper digit bitmaps. Accepts digits, '-', '.', 'x'. */
export function Led({ text, scale }) {
  const s = scale || 1;
  return (
    <LedBox style={{ '--s': s }} aria-label={String(text)}>
      {String(text)
        .split('')
        .map((ch, i) => {
          if (/\d/.test(ch)) return <img key={i} src={DIGITS[Number(ch)]} alt={ch} />;
          if (ch === '-') return <img key={i} src={digitDash} alt="-" />;
          if (ch === '.') return <i key={i} className="led__dot" />;
          return (
            <b key={i} className="led__glyph">
              {ch}
            </b>
          );
        })}
    </LedBox>
  );
}

const LedBox = styled.span`
  display: inline-flex;
  align-items: flex-end;
  gap: 1px;
  background: #000;
  border: 1px solid #808080;
  box-shadow: inset 1px 1px 0 #404040, 1px 1px 0 #fff;
  padding: 1px 2px;
  img {
    width: calc(13px * var(--s));
    height: calc(23px * var(--s));
    image-rendering: pixelated;
    display: block;
  }
  .led__dot {
    display: block;
    width: calc(4px * var(--s));
    height: calc(4px * var(--s));
    margin: 0 1px calc(2px * var(--s));
    background: ${T.led};
    box-shadow: 0 0 4px ${T.led};
  }
  .led__glyph {
    color: ${T.led};
    font: bold calc(16px * var(--s)) ${FONT.mono};
    line-height: calc(23px * var(--s));
    text-shadow: 0 0 4px ${T.led};
    padding: 0 2px;
  }
`;

/** The scrolling strip that every 2001 site had. Nothing important goes in it. */
export function Marquee({ children }) {
  return (
    <Scroll aria-hidden="true">
      <span>{children}</span>
    </Scroll>
  );
}

const Scroll = styled.div`
  overflow: hidden;
  white-space: nowrap;
  background: #000;
  border-top: 1px solid ${T.goldDeep};
  border-bottom: 1px solid ${T.goldDeep};
  color: ${T.gold};
  font: bold 11px ${FONT.web};
  letter-spacing: 1px;
  span {
    display: inline-block;
    padding: 3px 0 3px 100%;
    animation: cs-marquee 22s linear infinite;
  }
  @keyframes cs-marquee {
    to {
      transform: translateX(-100%);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
      padding-left: 8px;
    }
  }
`;

export function Blink({ children, className }) {
  return <Blinker className={className}>{children}</Blinker>;
}

const Blinker = styled.span`
  animation: cs-blink 1s steps(2, start) infinite;
  @keyframes cs-blink {
    to {
      visibility: hidden;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** An 88x31 web button. Two lines, one border, the whole internet of 2001 in a rectangle. */
export function Badge({ title, sub, on }) {
  return (
    <Web88 className={on ? 'on' : ''}>
      <b>{title}</b>
      {sub ? <span>{sub}</span> : null}
    </Web88>
  );
}

const Web88 = styled.span`
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 88px;
  height: 31px;
  padding: 0 6px;
  box-sizing: border-box;
  background: #000;
  border: 1px solid ${T.goldDeep};
  box-shadow: inset 1px 1px 0 #333, inset -1px -1px 0 #111;
  color: ${T.gold};
  font: 9px ${FONT.web};
  letter-spacing: 0.5px;
  line-height: 1.2;
  text-align: center;
  b {
    font-weight: 700;
    font-size: 10px;
  }
  span {
    color: #d9c27a;
  }
  &.on {
    background: ${T.gold};
    color: #000;
    border-color: #fff3c4;
  }
  &.on span {
    color: #4a3600;
  }
`;

/** The gold plate above a table: its name, and which table it is. */
export function Plaque({ title, sub, children }) {
  return (
    <Plate>
      <div className="pl__left">{children}</div>
      <div className="pl__name">
        <div className="pl__title">{title}</div>
        {sub ? <div className="pl__sub">{sub}</div> : null}
      </div>
    </Plate>
  );
}

const Plate = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 12px 0;
  padding: 6px 10px;
  background: linear-gradient(180deg, #fff3c4, #ffd86b 40%, #c9a227 100%);
  border: 1px solid #7a5c00;
  box-shadow: inset 0 1px 0 #fff8dc, 0 2px 0 #3b2a00, 0 3px 6px rgba(0, 0, 0, 0.5);
  color: #2b1d00;
  .pl__name {
    flex: 1;
    text-align: center;
  }
  .pl__title {
    font: bold 17px ${FONT.serif};
    letter-spacing: 3px;
    text-shadow: 0 1px 0 #fff2c0;
  }
  .pl__sub {
    font: 9px ${FONT.web};
    letter-spacing: 2px;
    color: #5a4200;
    margin-top: 1px;
  }
  .cs__back {
    ${xpButton};
    min-width: 0;
    padding: 0 9px;
  }
`;

/** A Win32 group box on the gray panel, the home of every control. */
export function GroupBox({ legend, className, children, style }) {
  return (
    <Group className={className} style={style}>
      <div className="gb__legend">{legend}</div>
      {children}
    </Group>
  );
}

const Group = styled.div`
  position: relative;
  border: 1px solid #d0d0bf;
  border-radius: 4px;
  padding: 12px 10px 10px;
  margin-top: 10px;
  background: ${T.gray};
  color: #000;
  font: 11px ${FONT.ui};
  .gb__legend {
    position: absolute;
    top: -7px;
    left: 8px;
    padding: 0 3px;
    background: ${T.gray};
    color: #0046d5;
    font: 11px ${FONT.ui};
  }
`;

/** The gray control panel under a felt table. Wraps group boxes so they read as one dialog. */
export const Panel = styled.div`
  background: ${T.gray};
  border: 1px solid #fff;
  outline: 1px solid ${T.grayDark};
  padding: 2px 10px 10px;
  margin-top: 12px;
  color: #000;
  font: 11px ${FONT.ui};
`;

/** An XP radio button drawn in CSS: the ring, the dot, the dotted focus. */
export function Radio({ name, value, checked, onChange, disabled, label }) {
  const id = `${name}-${value}`;
  return (
    <RadioBox className={disabled ? 'disabled' : ''}>
      <input type="radio" id={id} name={name} checked={checked} disabled={disabled} onChange={() => onChange(value)} />
      <label htmlFor={id}>
        <i className={`rd__ring${checked ? ' on' : ''}`} />
        {label}
      </label>
    </RadioBox>
  );
}

const RadioBox = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 12px;
  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
  label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font: 11px ${FONT.ui};
    color: #000;
    cursor: default;
    padding: 1px 2px;
  }
  input:focus + label {
    outline: 1px dotted #000;
  }
  .rd__ring {
    width: 13px;
    height: 13px;
    border-radius: 50%;
    box-sizing: border-box;
    border: 1px solid #1c5180;
    background: radial-gradient(circle at 40% 35%, #fff, #e3e9f5 70%, #c8d3e5);
    display: inline-block;
    position: relative;
  }
  .rd__ring.on::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 3px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: radial-gradient(circle at 40% 40%, #7bd35c, #21a121);
  }
  &.disabled label {
    color: #a0a0a0;
  }
  &.disabled .rd__ring {
    border-color: #c0c0c0;
    background: #f1f1ec;
  }
`;

// ── the bet controls ─────────────────────────────────────────────────────────

const CHIPS = [
  { v: '1', bg: '#f4f4f4', ink: '#333', ring: '#c9c9c9' },
  { v: '5', bg: '#c8102e', ink: '#fff', ring: '#ffd6d6' },
  { v: '25', bg: '#0b7a3a', ink: '#fff', ring: '#c9f0d6' },
  { v: '100', bg: '#1a1a1a', ink: '#ffd86b', ring: '#ffd86b' },
];

/** Stake entry. Everything typed here is the total spent — the fee is already inside it. */
export function StakeBox({ value, onChange, max, disabled, quick }) {
  const presets = quick || ['1', '5', '25', '100'];
  return (
    <GroupBox legend="Your bet">
      <Stake>
        <div className="sb__row">
          <label htmlFor="sb-amount">All-in:</label>
          <input
            id="sb-amount"
            value={value}
            disabled={disabled}
            onChange={e => onChange(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="0.00"
            inputMode="decimal"
          />
          <span className="sb__unit">{CHIP.symbol}</span>
          {max !== undefined && max !== null ? (
            <button type="button" className="sb__max" disabled={disabled} onClick={() => onChange(formatUnits(max, CHIP.decimals, 4))}>
              Max
            </button>
          ) : null}
        </div>
        <div className="sb__chips">
          {presets.map((p, i) => {
            const c = CHIPS[i % CHIPS.length];
            return (
              <button
                key={p}
                type="button"
                className="sb__chip"
                disabled={disabled}
                style={{ '--bg': c.bg, '--ink': c.ink, '--ring': c.ring }}
                onClick={() => onChange(p)}
                title={`${p} ${CHIP.symbol}`}
              >
                {p}
              </button>
            );
          })}
        </div>
        <div className="sb__note">Fee-inclusive: what you type is what you spend. Nothing is added at the end.</div>
      </Stake>
    </GroupBox>
  );
}

const Stake = styled.div`
  .sb__row {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  label {
    width: 44px;
    font: 11px ${FONT.ui};
  }
  input {
    ${sunken};
    flex: 1;
    min-width: 0;
    height: 21px;
    font: 12px ${FONT.mono};
    outline: none;
  }
  input:focus {
    border-color: #316ac5;
  }
  input:disabled {
    color: #7f7f7f;
    background: #f4f4f0;
  }
  .sb__unit {
    width: 34px;
    color: #000;
  }
  .sb__max {
    ${xpButton};
    min-width: 0;
    min-height: 21px;
    padding: 0 8px;
  }
  .sb__chips {
    display: flex;
    gap: 8px;
    margin: 9px 0 0 50px;
  }
  .sb__chip {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 0;
    padding: 0;
    cursor: pointer;
    color: var(--ink);
    font: bold 11px ${FONT.web};
    background: radial-gradient(circle at 50% 50%, var(--bg) 0 55%, var(--ring) 56% 62%, var(--bg) 63% 100%);
    box-shadow: 0 0 0 2px var(--bg), inset 0 0 0 1px rgba(255, 255, 255, 0.35), 0 2px 2px rgba(0, 0, 0, 0.45);
    position: relative;
  }
  .sb__chip::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: repeating-conic-gradient(from 0deg, var(--ring) 0 12deg, transparent 12deg 45deg);
    -webkit-mask: radial-gradient(circle, transparent 0 63%, #000 64% 100%);
    mask: radial-gradient(circle, transparent 0 63%, #000 64% 100%);
  }
  .sb__chip:not(:disabled):hover {
    transform: translateY(-2px);
  }
  .sb__chip:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .sb__note {
    margin: 8px 0 0 50px;
    font-size: 9px;
    color: #5a5a5a;
  }
`;

/** The one big button, plus whatever the lifecycle is currently doing. */
export function PlayButton({ label, onClick, disabled, phase, note, countdown }) {
  const busy = phase && phase !== 'idle' && phase !== 'done' && phase !== 'stuck';
  return (
    <Play>
      <button type="button" onClick={onClick} disabled={disabled || busy}>
        {busy ? '…' : label}
      </button>
      {busy ? (
        <div className="pb__note">
          <span className="pb__hourglass" aria-hidden="true">
            ⌛
          </span>
          {note}
          {phase === 'waiting' && countdown > 0 ? ` ${countdown}s` : ''}
        </div>
      ) : null}
    </Play>
  );
}

const Play = styled.div`
  margin-top: 12px;
  button {
    ${goldBevel};
    width: 100%;
    font-size: 16px;
    letter-spacing: 4px;
    padding: 9px 0;
    cursor: pointer;
  }
  button:not(:disabled):hover {
    background: linear-gradient(180deg, #fff8d8 0%, #ffe38a 38%, #e6b53a 62%, #c8961b 100%);
  }
  button:not(:disabled):active {
    border-style: inset;
  }
  button:disabled {
    background: linear-gradient(180deg, #e9e5d8, #cfc9b8);
    color: #8a8570;
    border-color: #d8d2c2;
    text-shadow: none;
    cursor: default;
  }
  .pb__note {
    margin-top: 6px;
    font: 11px ${FONT.ui};
    color: ${T.cream};
    text-align: center;
  }
  .pb__hourglass {
    display: inline-block;
    margin-right: 5px;
    animation: cs-turn 1.6s steps(4) infinite;
  }
  @keyframes cs-turn {
    to {
      transform: rotate(360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .pb__hourglass {
      animation: none;
    }
  }
`;

/** What happened, with the receipts — printed like one. */
export function Outcome({ result, children, onAgain }) {
  if (!result) return null;
  return (
    <Result className={result.won ? 'won' : 'lost'}>
      {result.won ? (
        <div className="rs__winner">
          <Blink>★ WINNER ★</Blink>
        </div>
      ) : null}
      <div className="rs__head">{result.won ? 'PAID' : 'NO PAYOUT'}</div>
      {children}
      <div className="rs__amount">
        {result.won ? `+${fmtChip(result.payout)} ${CHIP.symbol}` : `—`}
        {result.practice ? <span className="rs__play"> play money</span> : null}
      </div>
      <div className="rs__proof">
        {result.practice && result.source !== 'drand' ? (
          <>
            <b>PRACTICE</b> · the beacon could not be reached, so this play used randomness from
            your own browser · nothing on chain · play money
          </>
        ) : (
          <>
            {result.practice ? <b>PRACTICE · </b> : null}
            drand round <b>{result.round}</b> · randomness{' '}
            <span className="rs__hash">{String(result.randomness).slice(0, 18)}…</span>
            <a
              href={`https://api.drand.sh/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/public/${result.round}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              check it yourself
            </a>
            {result.practice ? (
              <div>settled in this browser with the contract&apos;s own arithmetic · nothing on chain · play money</div>
            ) : null}
          </>
        )}
      </div>
      <button type="button" className="rs__again" onClick={onAgain}>
        AGAIN
      </button>
    </Result>
  );
}

const Result = styled.div`
  margin: 12px 6px 0;
  background: #fff;
  color: #000;
  border: 1px solid #b9b9b9;
  border-top: 3px double #7a7a7a;
  border-bottom: 3px double #7a7a7a;
  padding: 10px 12px 12px;
  text-align: center;
  font-family: ${FONT.mono};
  position: relative;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.45);
  &.won {
    border-color: ${T.goldDeep};
  }
  .rs__winner {
    font: bold 14px ${FONT.sticker};
    letter-spacing: 4px;
    color: ${T.red};
    margin-bottom: 2px;
  }
  .rs__head {
    font-size: 11px;
    letter-spacing: 5px;
    color: #555;
  }
  &.won .rs__head {
    color: #0b7a3a;
  }
  .rs__amount {
    font: bold 22px ${FONT.serif};
    color: #000;
    margin: 4px 0 6px;
  }
  &.won .rs__amount {
    color: #0b7a3a;
  }
  .rs__play {
    font: 10px ${FONT.web};
    color: #7a5c00;
    letter-spacing: 1px;
  }
  .rs__proof {
    font-size: 9px;
    color: #444;
    line-height: 1.7;
    word-break: break-word;
  }
  .rs__proof b {
    color: #000;
  }
  .rs__proof a {
    color: #00f;
    margin-left: 6px;
  }
  .rs__again {
    ${xpButton};
    margin-top: 9px;
    letter-spacing: 2px;
  }
`;

/**
 * The disclosure strip: the edge, printed above the play button, in one line. `fair` flips it to
 * the sentence that is only true of COINFLIP. `edge` overrides the board line for a cabinet whose
 * board is not the 90% one (ROULETTE). `practice` stamps the line so a play-money floor never
 * reads as the live one; the receipt carries the rest of the practice disclosure.
 */
const EDGE_LINE =
  'This game is not even-money. Board pays 90.00%, 5% fee on top, ~85.5% expected return.';
const FAIR_LINE = "The coins are fair. The 5% fee is the house's entire edge.";

export function HouseRules({ fair, edge, practice }) {
  return (
    <Rules>
      <GroupBox legend="House rules">
        <div className="hr__title">
          HOUSE RULES{fair ? <span className="hr__seal">✔ FAIR COINS</span> : <span className="hr__seal hr__seal--edge">HOUSE EDGE</span>}
        </div>
        <div className={fair ? 'hr__headline' : 'hr__headline hr__headline--edge'}>
          {practice ? <b>PRACTICE. </b> : null}
          {fair ? FAIR_LINE : edge || EDGE_LINE} 18+.
        </div>
      </GroupBox>
    </Rules>
  );
}

const Rules = styled.div`
  margin-top: 4px;
  .hr__title {
    font: bold 11px ${FONT.ui};
    letter-spacing: 2px;
    color: #000;
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .hr__seal {
    font: bold 9px ${FONT.web};
    letter-spacing: 1px;
    color: #0b7a3a;
    border: 1px solid #0b7a3a;
    padding: 1px 6px;
    border-radius: 9px;
  }
  .hr__seal--edge {
    color: ${T.red};
    border-color: ${T.red};
  }
  .hr__headline {
    font: bold 11px ${FONT.ui};
    line-height: 1.5;
    color: #000;
  }
  .hr__headline--edge {
    color: #8a0000;
  }
  b {
    color: #000;
    font-weight: 700;
  }
`;

export { parseUnits };
